import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, ClickAwayListener, List, ListItem, Paper } from '@mui/material';
import { IOnePageStyles } from 'layouts/OnePagePreviewLayout';
import { useGetOnePageStyleButtons } from 'layouts/OnePagePreviewLayout/hooks/useGetOnePageStyleButtons';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan } from 'store/useBusinessToolStore/selectors';

import { Loader } from 'components/_basic/Loader/Loader';
import { useHeaderLogic } from 'components/Header/HeaderLogicProvider';

import { OnePageStyleSoon, OpenStyleSelectorButton, SelectOnePageStyleButton } from './components';
import { useUpdateOnePageStyle } from '../../hooks/useUpdateOnePageStyle';

import styles from './OnePageStyleSelector.module.scss';

export const OnePageStyleSelector: FC = () => {
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const { styleChangeHandler, isPending } = useUpdateOnePageStyle();
  const { handleClickUpgradePlan } = useHeaderLogic();
  const { t } = useTranslation();

  const businessPlan = useBusinessToolStore(getBusinessPlan);

  const { data: onePageButtons } = useGetOnePageStyleButtons();

  const businessPlanOnePageStyle = businessPlan.metadata.onePageTemplateStyle;

  const openMenuHandler = () => setIsMenuOpened(true);
  const clickAwayHandler = () => setIsMenuOpened(false);

  const updateOnePageStyle = async (style: IOnePageStyles, isAccessed: boolean) => {
    if (businessPlanOnePageStyle === style) return;

    if (!isAccessed) {
      setIsMenuOpened(false);
      handleClickUpgradePlan();
      return;
    }

    await styleChangeHandler(style);
    clickAwayHandler();
  };

  return (
    <Box className={styles['wrapper']}>
      <OpenStyleSelectorButton
        businessPlanOnePageStyle={businessPlanOnePageStyle}
        onClick={openMenuHandler}
        text={t('selectStyle')}
      />
      {isMenuOpened && (
        <ClickAwayListener onClickAway={clickAwayHandler}>
          <Paper elevation={2} className={styles['menu-wrapper']}>
            <List className={styles['menu-list']}>
              {isPending ? (
                <Box className={styles['loader-wrapper']}>
                  <Loader size={40} />
                </Box>
              ) : (
                <>
                  {onePageButtons.map(({ style, bgImage, isAccessed }) => {
                    const isSelected = style === businessPlanOnePageStyle;

                    return (
                      <ListItem className={styles['menu-item']} key={style}>
                        <SelectOnePageStyleButton
                          isUpgradeTextShown
                          bgImage={bgImage}
                          isSelected={isSelected}
                          isAccessed={isAccessed}
                          onClick={() => updateOnePageStyle(style, isAccessed)}
                        />
                      </ListItem>
                    );
                  })}
                  <ListItem className={styles['menu-item']}>
                    <OnePageStyleSoon />
                  </ListItem>
                </>
              )}
            </List>
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
};
